import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Check, Loader2, Send } from 'lucide-react';
import { toast } from 'sonner';

import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { useClientScope } from '@/context/ClientScopeProvider';
import { apiError } from '@/lib/api-error';
import { authenticatedBitswanBackendInstance } from '@/lib/api-client';
import {
  hoursLabel, periodLabel, type BillingSummary, type DirectContract,
} from '@/types/advisor-billing';
import { money } from '@/types/offer';

export default function ClientBillingPage() {
  const { clientId, client } = useClientScope();
  const queryClient = useQueryClient();

  const direct = useQuery<{ contracts: DirectContract[] }>({
    queryKey: ['direct-billing', clientId],
    queryFn: async () => {
      const api = await authenticatedBitswanBackendInstance();
      return (await api.get('/advisor/billing/direct/', { params: { client: clientId } })).data;
    },
  });

  const viaAoc = useQuery<BillingSummary>({
    queryKey: ['billing-summary', clientId],
    queryFn: async () => {
      const api = await authenticatedBitswanBackendInstance();
      return (await api.get('/advisor/billing/summary/', { params: { client: clientId } })).data;
    },
  });

  const markSent = useMutation({
    mutationFn: async ({ offer, period }: { offer: string; period: string }) => {
      const api = await authenticatedBitswanBackendInstance();
      return api.post('/advisor/billing/direct/', { offer, period });
    },
    onSuccess: () => {
      toast.success('Noted as invoiced.');
      queryClient.invalidateQueries({ queryKey: ['direct-billing', clientId] });
    },
    onError: (e) => toast.error(apiError(e, 'Could not mark it.')),
  });

  const contracts = direct.data?.contracts ?? [];
  const invoices = viaAoc.data?.invoices ?? [];

  return (
    <div className="space-y-6" data-testid="client-billing-page">
      <div>
        <h2 className="text-2xl font-bold tracking-tight">Billing</h2>
        <p className="text-muted-foreground">
          What {client.company_name} owes you month by month, and whether it has been invoiced and paid.
        </p>
      </div>

      {direct.isLoading || viaAoc.isLoading ? (
        <div className="flex justify-center py-12"><Loader2 className="h-6 w-6 animate-spin text-muted-foreground" /></div>
      ) : contracts.length === 0 && invoices.length === 0 ? (
        <p className="py-12 text-center text-sm text-muted-foreground" data-testid="client-billing-empty">
          Nothing to bill yet. Once {client.company_name} accepts an offer, its months appear here.
        </p>
      ) : null}

      {contracts.map((contract) => (
        <Card key={contract.offer_id} data-testid={`direct-contract-${contract.offer_id}`}>
          <CardHeader className="pb-3">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <CardTitle className="text-base">{contract.title}</CardTitle>
              <Badge variant="outline">You invoice them directly</Badge>
            </div>
            <p className="text-xs text-muted-foreground">
              {contract.package} package · {contract.currency}{contract.ended_at ? ' · ended' : ''}
            </p>
          </CardHeader>
          <CardContent>
            <table className="w-full text-sm">
              <thead className="text-left text-xs text-muted-foreground">
                <tr>
                  <th className="py-1">Month</th>
                  <th className="py-1">Package</th>
                  <th className="py-1">Hours</th>
                  <th className="py-1 text-right">Owed</th>
                  <th className="py-1 text-right">Invoice</th>
                </tr>
              </thead>
              <tbody>
                {[...contract.months].reverse().map((month) => (
                  <tr key={month.period} className="border-t" data-testid={`direct-month-${contract.offer_id}-${month.period}`}>
                    <td className="py-2 font-medium">{periodLabel(month.period)}</td>
                    <td className="py-2">{money(month.package_amount, month.currency)}</td>
                    <td className="py-2">{hoursLabel(month.hours)}{parseFloat(month.hours_amount) > 0 ? ` · ${money(month.hours_amount, month.currency)}` : ''}</td>
                    <td className="py-2 text-right font-semibold">{money(month.total, month.currency)}</td>
                    <td className="py-2 text-right">
                      {month.sent_at ? (
                        <span className="inline-flex items-center gap-1 text-xs text-emerald-700 dark:text-emerald-400" data-testid={`direct-sent-${contract.offer_id}-${month.period}`}>
                          <Check className="h-3.5 w-3.5" />
                          Sent {new Date(month.sent_at).toLocaleDateString()}
                          {month.sent_total !== month.total && (
                            <span className="text-muted-foreground"> for {money(month.sent_total, month.currency)}</span>
                          )}
                        </span>
                      ) : (
                        <Button size="sm" variant="outline" disabled={markSent.isPending}
                          onClick={() => markSent.mutate({ offer: contract.offer_id, period: month.period })}
                          data-testid={`mark-sent-${contract.offer_id}-${month.period}`}>
                          <Send className="mr-2 h-3.5 w-3.5" /> Invoice sent
                        </Button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </CardContent>
        </Card>
      ))}

      {invoices.length > 0 && (
        <Card data-testid="aoc-invoices">
          <CardHeader className="pb-3">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <CardTitle className="text-base">Billed through BitSwan</CardTitle>
              <Badge variant="outline">On their BitSwan invoice</Badge>
            </div>
            <p className="text-xs text-muted-foreground">
              Your packages and hours go on the invoice BitSwan sends them each month. Once they pay, the amount is available for payout,
              less the processing fee BitSwan keeps out of your share.
            </p>
          </CardHeader>
          <CardContent>
            <table className="w-full text-sm">
              <thead className="text-left text-xs text-muted-foreground">
                <tr>
                  <th className="py-1">Month</th>
                  <th className="py-1">Invoice</th>
                  <th className="py-1 text-right">Your share</th>
                  <th className="py-1 text-right">Fee</th>
                  <th className="py-1 text-right">Yours</th>
                  <th className="py-1 text-right">Status</th>
                </tr>
              </thead>
              <tbody>
                {invoices.map((row) => (
                  <tr key={row.invoice_id} className="border-t" data-testid={`aoc-invoice-${row.invoice_number}`}>
                    <td className="py-2 font-medium">{periodLabel(row.billing_period)}</td>
                    <td className="py-2 text-muted-foreground">{row.invoice_number}</td>
                    <td className="py-2 text-right">{money(row.my_share, row.currency)}</td>
                    <td className="py-2 text-right text-muted-foreground">{money(row.processing_fee, row.currency)}</td>
                    <td className="py-2 text-right font-semibold">{money(row.my_net, row.currency)}</td>
                    <td className="py-2 text-right">
                      <Badge className={row.status === 'paid' ? 'bg-emerald-500/15 text-emerald-700 dark:text-emerald-400' : 'bg-amber-500/15 text-amber-700 dark:text-amber-400'} data-testid={`aoc-invoice-status-${row.invoice_number}`}>
                        {row.status === 'paid' ? `Paid ${row.paid_at ? new Date(row.paid_at).toLocaleDateString() : ''}` : row.status === 'overdue' ? 'Overdue' : `Due ${new Date(row.due_date).toLocaleDateString()}`}
                      </Badge>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
